import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Loader2, Mail, ScanText, ArrowUpRight, Plus, RefreshCw, X, Copy } from "lucide-react";

const MOD = {
  largeformat: { label: "Large Format", cartLabel: "Gran Formato", route: "/large-format", inline: true },
  stickers: { label: "Stickers", cartLabel: "Stickers", route: "/stickers", inline: true },
  booklet: { label: "Booklet", cartLabel: "Booklet", route: "/booklet", inline: false },
  paper: { label: "Paper", cartLabel: "Paper", route: "/paper", inline: false },
  unassigned: { label: "Unassigned", cartLabel: "", route: null, inline: false },
};
const GMODES = [
  ["4_corners", "4 Corners (4)"], ["6_grommets", "4 Corners + 2 Middle (6)"],
  ["top_corners", "Top Corners (2)"], ["top_center", "Top Center (1)"], ["every_2ft", "Every 2 ft"],
];

export default function QuoteIntake() {
  const cart = useCart();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [mats, setMats] = useState({ largeformat: null, stickers: null });

  useEffect(() => {
    (async () => {
      try {
        const [lf, stk] = await Promise.all([
          api.get("/roll-materials?module=large-format"),
          api.get("/roll-materials?module=stickers"),
        ]);
        const pick = (res) => { const a = res.data || []; const d = a.find((m) => m.is_default && m.printable_width) || a.find((m) => m.printable_width) || a[0]; return d?.id || null; };
        setMats({ largeformat: pick(lf), stickers: pick(stk) });
      } catch (e) { /* materials optional until pricing */ }
    })();
  }, []);

  const priceRow = async (row) => {
    const meta = MOD[row.module];
    if (!meta?.inline) return null;
    const matId = mats[row.module];
    if (!matId || !(+row.width > 0 && +row.height > 0)) return null;
    const isSticker = row.module === "stickers";
    const body = {
      sizes: [{ width: +row.width, height: +row.height, qty: +row.qty, grommets: !!row.grommets, grommet_mode: row.grommet_mode || "", diecut: false, transfer_tape: false, laminate: false }],
      mode: isSticker ? "print_cut" : "print", material_ids: [matId], sticker: isSticker, designs: 1, ink_coverage_pct: 100,
    };
    const { data } = await api.post("/calc/largeformat", body);
    const r = (data.results || [])[0];
    return r ? { price: r.total.selling_price, summary: r } : null;
  };

  const repriceAt = async (i, snapshot) => {
    const row = snapshot[i];
    if (!MOD[row.module]?.inline || !(+row.width > 0 && +row.height > 0)) {
      setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, price: undefined, summary: undefined } : r)));
      return;
    }
    let price, summary;
    try { const p = await priceRow(row); price = p?.price; summary = p?.summary; } catch (e) { price = undefined; }
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, price, summary } : r)));
  };

  const read = async () => {
    if (!files.length && !text.trim()) { toast.error("Upload a screenshot/PDF or paste the request"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (text.trim()) fd.append("text", text.trim());
      const r = await api.post("/quote-intake/parse", fd);
      const got = (r.data?.items || []).map((x) => ({ ...x }));
      if (!got.length) { toast.error("The AI could not read line items"); setLoading(false); return; }
      setRows(got);
      toast.success(`Read ${got.length} line item${got.length > 1 ? "s" : ""}`);
      got.forEach((_, i) => repriceAt(i, got));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not read the request"); }
    setLoading(false);
  };

  const update = (i, patch) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    setRows(next);
    if ("width" in patch || "height" in patch || "qty" in patch || "grommets" in patch || "grommet_mode" in patch || "module" in patch) repriceAt(i, next);
  };

  const addRow = async (i) => {
    const row = rows[i]; const meta = MOD[row.module];
    if (meta?.inline) {
      const p = await priceRow(row);
      if (!p) { toast.error("Set a valid size to price this line"); return; }
      cart.addItem({ module: meta.cartLabel, title: `${row.product || meta.label} · ${row.width}×${row.height}" ×${row.qty}${row.grommets ? " · grommets" : ""}`, price: p.price, summary: p.summary });
      toast.success("Added to quote");
    } else if (meta?.route) { navigate(meta.route); }
    else { toast.error("Assign a module first"); }
  };

  const addAllInline = async () => {
    let n = 0;
    for (const row of rows) {
      if (!MOD[row.module]?.inline) continue;
      const p = await priceRow(row); if (!p) continue;
      const meta = MOD[row.module];
      cart.addItem({ module: meta.cartLabel, title: `${row.product || meta.label} · ${row.width}×${row.height}" ×${row.qty}${row.grommets ? " · grommets" : ""}`, price: p.price, summary: p.summary });
      n++;
    }
    n ? toast.success(`Added ${n} priced line${n > 1 ? "s" : ""} to the quote`) : toast.error("No priced LF/Sticker lines to add");
  };

  const buildClientText = () => {
    const groups = {};
    (rows || []).forEach((r) => { if (r.price != null) { const l = MOD[r.module]?.label || "Other"; (groups[l] = groups[l] || []).push(r); } });
    const lines = ["Quote estimate — Print and Save", ""];
    let grand = 0;
    Object.entries(groups).forEach(([l, rs]) => {
      lines.push(`${l}:`);
      let sub = 0;
      rs.forEach((r) => { sub += r.price; grand += r.price; lines.push(` • ${r.product || l} — ${r.width}x${r.height}" x${r.qty}${r.grommets ? " · grommets" : ""} — ${money(r.price)}`); });
      lines.push(`  Subtotal ${l}: ${money(sub)}`, "");
    });
    lines.push(`Estimated total: ${money(grand)}`);
    const pending = (rows || []).filter((r) => !MOD[r.module]?.inline);
    if (pending.length) lines.push("", `Plus ${pending.length} item(s) quoted separately: ${pending.map((r) => r.product || MOD[r.module]?.label).join(", ")}.`);
    return lines.join("\n");
  };

  const copyForClient = async () => {
    const priced = (rows || []).some((r) => r.price != null);
    if (!priced) { toast.error("No priced lines to copy yet"); return; }
    try { await navigator.clipboard.writeText(buildClientText()); toast.success("Quote text copied — paste it in your reply"); }
    catch (e) { toast.error("Could not copy to clipboard"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl mb-6 overflow-hidden" data-testid="quote-intake">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-gradient-to-r from-[#2495D3]/8 to-transparent">
        <Sparkles size={16} className="text-[#2495D3]" />
        <h3 className="font-head font-bold text-sm">AI Intake — read a customer email / screenshot</h3>
      </div>
      <div className="p-5 space-y-3">
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <label className="flex items-center justify-center gap-2 h-20 rounded-lg border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#2495D3] text-sm text-slate-500" data-testid="intake-drop">
              <Mail size={18} />{files.length ? `Add more (${files.length} attached)` : "Upload screenshots (PNG/JPG) or PDF"}
              <input data-testid="intake-file" type="file" multiple accept="image/png,image/jpeg,image/webp,application/pdf" onChange={(e) => { const sel = Array.from(e.target.files || []); if (sel.length) setFiles((prev) => [...prev, ...sel].slice(0, 10)); e.target.value = ""; }} className="hidden" />
            </label>
            {files.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="intake-files">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 rounded-full pl-2 pr-1 py-0.5" data-testid={`intake-file-chip-${i}`}>
                    <ScanText size={11} className="text-[#2495D3]" /><span className="max-w-[130px] truncate">{f.name}</span>
                    <button type="button" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500" data-testid={`intake-file-remove-${i}`}><X size={12} /></button>
                  </span>
                ))}
                <button type="button" onClick={() => setFiles([])} className="text-[11px] text-slate-400 hover:text-red-500 underline" data-testid="intake-files-clear">Clear all</button>
              </div>
            )}
            <p className="text-[10px] text-slate-400">Tip: add several screenshots from the same conversation (e.g. the request + the reply with quantities) — the AI merges them.</p>
          </div>
          <Textarea data-testid="intake-text" value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="…or paste the email text here" className="rounded-lg text-sm" />
        </div>
        <Button data-testid="intake-read" onClick={read} disabled={loading} className="w-full rounded-lg bg-[#2495D3] hover:bg-[#1E7AA9]">
          {loading ? <><Loader2 size={15} className="mr-1.5 animate-spin" />Reading with AI…</> : <><ScanText size={15} className="mr-1.5" />Read with AI</>}
        </Button>

        {rows && rows.length > 0 && (
          <div className="border border-slate-200 rounded-lg overflow-auto" data-testid="intake-review">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase text-slate-400">
                  <th className="text-left px-2 py-2">Module</th><th className="text-left px-2 py-2">Item</th>
                  <th className="px-1 py-2">W</th><th className="px-1 py-2">H</th><th className="px-1 py-2">Qty</th>
                  <th className="text-left px-2 py-2">Grommets</th><th className="text-right px-2 py-2">Price</th><th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const meta = MOD[r.module] || MOD.unassigned;
                  return (
                    <tr key={i} data-testid={`intake-row-${i}`} className={`border-b border-slate-100 ${r.module === "unassigned" ? "bg-amber-50" : ""}`}>
                      <td className="px-2 py-2">
                        <Select value={r.module} onValueChange={(v) => update(i, { module: v })}>
                          <SelectTrigger data-testid={`intake-module-${i}`} className="h-7 text-xs rounded w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>{Object.keys(MOD).map((k) => <SelectItem key={k} value={k} className="text-xs">{MOD[k].label}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-2 min-w-[150px]">
                        <Input data-testid={`intake-name-${i}`} value={r.product} onChange={(e) => update(i, { product: e.target.value })} className="h-7 text-xs rounded" />
                        {r.confidence === "low" && <span className="text-[9px] text-amber-500" title={r.notes}>low confidence · {r.notes}</span>}
                      </td>
                      <td className="px-1 py-2"><Input data-testid={`intake-w-${i}`} type="number" value={r.width} onChange={(e) => update(i, { width: e.target.value })} className="h-7 w-14 text-xs rounded num" /></td>
                      <td className="px-1 py-2"><Input data-testid={`intake-h-${i}`} type="number" value={r.height} onChange={(e) => update(i, { height: e.target.value })} className="h-7 w-14 text-xs rounded num" /></td>
                      <td className="px-1 py-2"><Input data-testid={`intake-qty-${i}`} type="number" value={r.qty} onChange={(e) => update(i, { qty: e.target.value })} className="h-7 w-14 text-xs rounded num" /></td>
                      <td className="px-2 py-2">
                        {meta.inline ? (
                          <div className="flex items-center gap-1">
                            <input type="checkbox" data-testid={`intake-grommets-${i}`} checked={!!r.grommets} onChange={(e) => update(i, { grommets: e.target.checked, grommet_mode: e.target.checked ? (r.grommet_mode || "4_corners") : "" })} />
                            {r.grommets && (
                              <Select value={r.grommet_mode || "4_corners"} onValueChange={(v) => update(i, { grommet_mode: v })}>
                                <SelectTrigger data-testid={`intake-grommet-mode-${i}`} className="h-6 text-[10px] rounded w-36"><SelectValue /></SelectTrigger>
                                <SelectContent>{GMODES.map(([k, l]) => <SelectItem key={k} value={k} className="text-xs">{l}</SelectItem>)}</SelectContent>
                              </Select>
                            )}
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-2 text-right num font-semibold" data-testid={`intake-price-${i}`}>
                        {meta.inline ? (r.price != null ? money(r.price) : <RefreshCw size={12} className="inline text-slate-300 animate-spin" />) : <span className="text-slate-300 text-[10px]">module</span>}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {meta.inline
                          ? <Button data-testid={`intake-add-${i}`} size="sm" variant="outline" className="h-7 rounded text-[11px]" onClick={() => addRow(i)}><Plus size={12} className="mr-1" />Add</Button>
                          : meta.route
                            ? <Button data-testid={`intake-open-${i}`} size="sm" variant="outline" className="h-7 rounded text-[11px]" onClick={() => addRow(i)}>Open<ArrowUpRight size={12} className="ml-1" /></Button>
                            : <span className="text-[10px] text-amber-600">assign</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(() => {
              const byMod = {};
              rows.forEach((r) => { if (r.price != null) { const l = MOD[r.module]?.label || "Other"; byMod[l] = (byMod[l] || 0) + r.price; } });
              const grand = rows.reduce((a, r) => a + (r.price != null ? r.price : 0), 0);
              const pending = rows.filter((r) => !MOD[r.module]?.inline).length;
              const mods = Object.entries(byMod);
              return (
                <div className="px-3 py-2 border-t border-slate-100 bg-white" data-testid="intake-totals">
                  <div className="flex flex-wrap items-center gap-2">
                    {mods.map(([l, v], idx) => (
                      <span key={l} className="inline-flex items-center gap-1.5 text-[11px] rounded-full bg-slate-100 px-2.5 py-1" data-testid={`intake-subtotal-${idx}`}>
                        <span className="text-slate-500">{l}</span><span className="num font-semibold text-slate-700">{money(v)}</span>
                      </span>
                    ))}
                    <span className="ml-auto flex items-center gap-2">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Estimated total</span>
                      <span className="num text-xl font-black text-[#2495D3]" data-testid="intake-grand-total">{money(grand)}</span>
                    </span>
                  </div>
                  {pending > 0 && <p className="text-[10px] text-amber-600 mt-1" data-testid="intake-pending-note">{pending} Paper/Booklet/unassigned line(s) not included yet — quote them in their module.</p>}
                </div>
              );
            })()}
            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-t border-slate-100">
              <span className="text-[11px] text-slate-500">LF &amp; Stickers price inline · Paper &amp; Booklet open in their module to pick stock.</span>
              <div className="flex items-center gap-2">
                <Button data-testid="intake-copy" size="sm" variant="outline" className="h-7 rounded text-[11px]" onClick={copyForClient}><Copy size={13} className="mr-1" />Copy for client</Button>
                <Button data-testid="intake-add-all" size="sm" className="h-7 rounded text-[11px] bg-[#2495D3] hover:bg-[#1E7AA9]" onClick={addAllInline}>Add all priced lines</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
